// The sentences under the stock pill's expansion (PLAN-UI §28.3, U8;
// screen-mock `#stockX`).
//
// **The prose is a pure function, for `hand.ts`'s and `selection.ts`'s
// reason.** These two lines are the whole of what the dying dock said while
// nothing was selected — a count of everything, where it is, and what the
// factory owes — and every one of them is a plural, an omission or a word
// chosen for a number. Written straight into JSX that is a set of ternaries
// nothing can assert about; written here it is a test.
//
// No React and no DOM.
//
// **A zero phrase is omitted rather than written as "nothing"** (fewer words
// in the interface, the standing instruction from U3). The dock said "Nothing
// left in the heaps you are standing on", which is a sentence about a number
// being nought — the true reading of a missing phrase is that there is none.
// `belts` is the exception and is always said: parts laid is the one number a
// player is deciding against every turn, and a factory with none is a factory
// that has not started.

import { type ShelfTotals, totalUnits } from "src/engine/session/shelfTotals";

/** `1 thing` / `2 things`, the only pluralisation this file does. */
const count = (n: number, one: string, many: string = `${one}s`): string =>
  `${n} ${n === 1 ? one : many}`;

/**
 * The resting sentences, in the pieces the panel bolds separately.
 *
 * Split rather than returned as one string because the emphasis is not
 * uniform: the headline is bold ink, the artifact clause is brand, and the
 * rest is the secondary voice. A single sentence would have to be re-parsed
 * by the component to draw it.
 */
export interface StockProse {
  /** "22 units across 5 pieces" — bold in the panel. */
  headline: string;
  /** ", 3 of it in an oven", or "" when no oven is running. */
  cooking: string;
  /**
   * "9 belt parts laid · 2 drawn awaiting iron · 4 scrap under your miners ·
   * 3 board tiles to place" — every zero phrase omitted.
   */
  work: string;
}

/**
 * The two lines under the buckets.
 *
 * `tiles` is `state.run?.tiles ?? 0` — Free Space tiles banked. **The one
 * counter from the dock's have-row that survives**, and as a phrase rather
 * than a chip: a player holding tiles has no other way to learn it, and the
 * turn it stops being nought is the turn it matters most.
 */
export function stockProse(totals: ShelfTotals, tiles: number): StockProse {
  const units = totalUnits(totals);

  const work: string[] = [
    // Always said — see the header note.
    `${count(totals.belts, "belt part")} laid`
  ];
  if (totals.drawn > 0) work.push(`${totals.drawn} drawn awaiting iron`);
  // **"scrap" is a mass noun and takes no plural** — the mock's own wording,
  // and "4 scraps" is the reading a bare `count` would have produced.
  if (totals.underfoot > 0) work.push(`${totals.underfoot} scrap under your miners`);
  if (tiles > 0) work.push(`${count(tiles, "board tile")} to place`);

  return {
    headline: `${count(units, "unit")} across ${count(totals.loaded, "piece")}`,
    cooking: totals.cooking > 0 ? `, ${totals.cooking} of it in an oven` : "",
    work: work.join(" · ")
  };
}

/**
 * How many pieces an army has on the board.
 *
 * `totals.pieces` is indexed by piece type and index 0 is never used, so the
 * sum is the army. Its own function because the watching row is the only
 * caller and a `reduce` inlined in a `map` is arithmetic in the JSX.
 */
export const armyPieces = (totals: ShelfTotals): number =>
  totals.pieces.reduce((sum, n) => sum + n, 0);
